/* eslint-disable no-unused-vars */

import * as d3 from 'd3';
import _ from 'lodash';
import { swoopyDrag } from '../swoopy-drag';
import { wordwrap } from 'd3-jetpack';
import { intcomma, apmonth, apdate } from 'journalize';
import * as moment from 'moment';
import data from '../../../data/data.json';

// function for unemployment rate chart
function render() {
  // a reference to the default graphic container, change if needed
  const container = d3.select('#unemployment-claims-chart');

  // a helper function to grab the container's width
  const getFrameWidth = () => container.node().offsetWidth;

  // a helper function to clear the container of its contents
  const clearContainer = () => container.html('');

  clearContainer();

  //set margins
  let margin = {
    top: 15,
    left: 100,
    bottom: 40,
  };

  let breakPoint = 500;

  // grab the data we need
  let claimsData = data.claims;

  // changing to moment date+time objects
  claimsData.forEach(d => (d.date = moment(d.date, 'M/D/YY')));

  // grabbing totals and ranges
  let dateRange = d3.extent(claimsData, d => d.date),
    totalsMax = d3.max(claimsData, d => d.claims);

  // set ranges
  let x = d3.scaleTime().domain(dateRange);
  let y = d3
    .scaleLinear()
    .domain([0, 300000]) //totalsMax
    .nice();

  const dates = {
    begin: dateRange[0],
    end: dateRange[1],
  };

  const scaleNum = {
    harvey: 63788,
    recession: 49398,
    intro: 120000,
  };

  // append svg
  let svg = container.append('svg'),
    g = svg.append('g').attr('class', 'unemployment-claims-bar-chart');

  let xAxis = g.append('g').attr('class', 'axis axis--x'),
    yAxis = g.append('g').attr('class', 'axis axis--y');

  let bars = g
    .selectAll('.bar')
    .data(claimsData)
    .enter()
    .append('rect')
    .attr('class', 'bar')
    .attr('fill', function(d) {
      if (d.date > moment('3/15/20', 'M/D/YY')) {
        return '#529aad';
      }
      return '#A1D1DF';
    });

  let frameWidth = getFrameWidth(),
    frameHeight;

  if (frameWidth > breakPoint) {
    frameHeight = 350; //400
    margin.left = 50;
    margin.right = 130; //85
  } else {
    frameHeight = 300;
    margin.left = 50;
    margin.right = 110;
  }

  let chartWidth = frameWidth - margin.left - margin.right,
    chartHeight = frameHeight - margin.bottom;

  // function to format dates, called in xAxis.call(d3.tickFormat())
  function customDateFormat(d) {
    let year = moment(d).format('YYYY');
    let month = moment(d).format('MMMM');
    if (
      (month == 'October' && year == '2020') |
      (month == 'July' && year == '2019') |
      (month == 'January' && frameWidth > breakPoint)
    ) {
      return `${month}
      ${year}`;
    }
  }

  svg.attr('width', frameWidth).attr('height', frameHeight);
  g.attr('width', chartWidth).attr(
    'transform',
    `translate(${margin.left},${margin.top})`
  );

  x.range([
    chartWidth / claimsData.length / 2,
    chartWidth - chartWidth / claimsData.length / 2,
  ]);
  y.range([chartHeight, 0]);

  y.range([chartHeight, 0]);

  bars
    .attr('width', function(d) {
      return (chartWidth / claimsData.length) * 0.75;
    })
    .attr('x', d => x(d.date) - chartWidth / claimsData.length / 2)
    .attr('y', d => y(d.claims))
    .attr('height', d => chartHeight - y(d.claims));

  xAxis
    .call(
      d3
        .axisBottom(x)
        .ticks(10)
        .tickFormat(customDateFormat) //frameWidth < breakPoint ? customMobileDateFormat : customDateFormat
    )
    .attr('transform', `translate(0,${frameHeight - margin.bottom})`);

  yAxis.call(
    d3
      .axisLeft(y)
      .ticks(3)
      .tickSize(-chartWidth)
    //      .tickFormat(customNumFormat)
  );

  // adding annotation lines
  const annotation = g.append('g').attr('class', 'annotations');

  annotation
    .append('line')
    .attr('class', 'annotation-line')
    .attr('stroke', '#212121')
    .attr('stroke-dasharray', '3px')
    .attr('stroke-width', '1px')
    .attr('x1', x(moment(dates.begin, 'M/D/YYYY')))
    .attr('y1', y(scaleNum.harvey))
    .attr('x2', x(moment(dates.end), 'M/D/YYYY') + 5)
    .attr('y2', y(scaleNum.harvey));

  annotation
    .append('text')
    .attr('class', 'annotation-label')
    .attr(
      'transform',
      'translate(' +
      (frameWidth > breakPoint
        ? x(moment(dates.end, 'M/D/YYYY')) + 10
        : x(moment(dates.end, 'M/D/YYYY')) + 10) +
      ',' +
      y(scaleNum.intro + (frameWidth > breakPoint ? 1000 : 2000)) + //frameWidth > breakPoint ? 1000 : 10000
        ')'
    )
    .tspans(wordwrap(`Weekly claims previously peaked in:`, 15))
    .attr('font-size', frameWidth > breakPoint ? '11px' : '10px');

  annotation
    .append('text')
    .attr('class', 'annotation-label')
    .attr(
      'transform',
      'translate(' +
        (frameWidth > breakPoint
          ? x(moment(dates.end, 'M/D/YYYY')) + 10
          : x(moment(dates.end, 'M/D/YYYY')) + 10) +
        ',' +
        y(scaleNum.harvey + 10000) +
        ')'
    )
    .tspans([`Hurricane Harvey`, `2017`], 11)
    .attr('font-size', frameWidth > breakPoint ? '11px' : '10px');

  annotation
    .append('line')
    .attr('class', 'annotation-line')
    .attr('stroke', '#212121')
    .attr('stroke-dasharray', '3px')
    .attr('stroke-width', '1px')
    .attr('x1', x(moment(dates.begin, 'M/D/YYYY')))
    .attr('y1', y(scaleNum.recession))
    .attr('x2', x(moment(dates.end), 'M/D/YYYY') + 5)
    .attr('y2', y(scaleNum.recession));

  annotation
    .append('text')
    .attr('class', 'annotation-label')
    .attr(
      'transform',
      'translate(' +
        (frameWidth > breakPoint
          ? x(moment(dates.end, 'M/D/YYYY')) + 10
          : x(moment(dates.end, 'M/D/YYYY')) + 10) +
        ',' +
        y(scaleNum.recession - 7000) +
        ')'
    )
    .tspans([`2008`, `Great Recession`], 11)
    .attr('font-size', frameWidth > breakPoint ? '11px' : '10px');

  // adding labels

  let labels = g.append('g').attr('class', 'labeled-elements');

  let labelElements = labels
    .selectAll('g')
    .data(getLabeledData(claimsData, ['']))
    .enter()
    .append('g')
    .attr('class', 'label-element');

  let labelText = labelElements.append('g').attr('class', 'label-text');

  labelText
    .append('text')
    .text(d => `4 million claims`)
    .attr('font-weight', 700);

  let dateAnno = labelText.append('text');

  labelElements
    .attr('transform', d => `translate(${x(d.date)},${y(d.claims + 200000)})`)
    .attr('font-size', frameWidth > breakPoint ? '13px' : '12px');

  labelText
    .attr('transform', (d, i) => {
      if (i === 0 && frameWidth > breakPoint) {
        return 'translate(20, 65)';
      } else return 'translate(10, 30)';
    })
    .attr('text-anchor', 'start');

  dateAnno.attr('y', frameWidth > breakPoint ? 13 : 13);

  dateAnno.each(function(d) {
    d3.select(this)
      .text('')
      .attr('font-weight', d.weight)
      .tspans(wordwrap(`from March 15 to Dec. 19`, 15));
  });
}

function getLabeledData(data, dates) {
  // always annotate the latest datapoint
  let labeledData = [data[data.length - 2]];
  dates.forEach(date => {
    date = moment(date, 'M/D/YY');
    let foundData = data.find(d => d.date.isSame(date));
    if (foundData) {
      labeledData.push(foundData);
    }
  });
  return labeledData;
}

export default render;
